import type { ImageSegmenter } from '@mediapipe/tasks-vision';

// Live video effects for calls: the camera frames are redrawn through a canvas (colour filter, and a blurred
// or replaced background) and the canvas becomes the video track that is sent. Runs in the page (no server
// ever sees the picture); with nothing chosen the camera track is sent as it is, at no cost.

export const VIDEO_EFFECTS = ['none', 'warm', 'cool', 'mono', 'vintage', 'vivid', 'soft', 'party'] as const;
export type VideoEffect = (typeof VIDEO_EFFECTS)[number];

const FILTERS: Record<Exclude<VideoEffect, 'none' | 'party'>, string> = {
  warm: 'sepia(0.25) saturate(1.25) hue-rotate(-8deg) brightness(1.03)',
  cool: 'saturate(1.1) hue-rotate(14deg) brightness(1.04)',
  mono: 'grayscale(1) contrast(1.12)',
  vintage: 'sepia(0.55) contrast(0.95) brightness(1.06) saturate(0.85)',
  vivid: 'saturate(1.65) contrast(1.08)',
  soft: 'brightness(1.08) contrast(0.92) saturate(1.05) blur(0.6px)',
};

/** The CSS filter of an effect at a moment (party slowly cycles the colours). */
export function effectFilter(effect: VideoEffect, t = 0): string {
  if (effect === 'none') return 'none';
  if (effect === 'party') return `hue-rotate(${Math.round((t / 20) % 360)}deg) saturate(1.5) contrast(1.05)`;
  return FILTERS[effect];
}

export function effectsSupported(): boolean {
  if (typeof document === 'undefined') return false;
  const canvas = document.createElement('canvas');
  return typeof canvas.captureStream === 'function' && 'filter' in (canvas.getContext('2d') ?? {});
}

/** What goes behind the person. */
export type BackgroundLayer = { kind: 'none' } | { kind: 'blur'; radius: number } | { kind: 'image'; image: ImageBitmap };

/** Person probability → mask opacity, with a soft edge instead of a hard cut-out. */
export function maskAlpha(confidence: number): number {
  const a = (confidence - 0.35) / 0.3;
  return Math.round(255 * (a <= 0 ? 0 : a >= 1 ? 1 : a * a * (3 - 2 * a)));
}

/**
 * Draws a camera track through an effect into a canvas track. A timer drives it (not animation frames, which
 * stop in a background tab; pages in a call keep running timers), at the camera's frame rate up to 30 fps.
 */
export class VideoEffectProcessor {
  readonly output: MediaStreamTrack;
  private readonly video = document.createElement('video');
  private readonly canvas = document.createElement('canvas');
  private readonly ctx: CanvasRenderingContext2D;
  private readonly work = new OffscreenCanvas(1, 1);
  private readonly person = new OffscreenCanvas(1, 1);
  private readonly mask = new OffscreenCanvas(1, 1);
  private maskReady = false;
  private background: BackgroundLayer = { kind: 'none' };
  private segmenter: ImageSegmenter | null = null;
  private lastTimestamp = 0;
  private slow = false;
  private frameNo = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;

  constructor(
    private readonly source: MediaStreamTrack,
    private effect: VideoEffect,
  ) {
    const settings = source.getSettings();
    this.canvas.width = settings.width ?? 1280;
    this.canvas.height = settings.height ?? 720;
    this.ctx = this.canvas.getContext('2d', { alpha: false })!;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.srcObject = new MediaStream([source]);
    void this.video.play().catch(() => undefined);
    const fps = Math.min(30, settings.frameRate ?? 30);
    this.output = this.canvas.captureStream(fps).getVideoTracks()[0]!;
    const frame = () => {
      if (this.stopped) return;
      this.draw();
      this.timer = setTimeout(frame, 1000 / fps);
    };
    frame();
  }

  setEffect(effect: VideoEffect): void {
    this.effect = effect;
  }

  /** A background needs the segmenter; without one the layer is ignored (the plain picture is sent). */
  setBackground(layer: BackgroundLayer, segmenter: ImageSegmenter | null): void {
    this.background = layer;
    this.segmenter = segmenter;
    this.maskReady = false;
  }

  private segment(): void {
    const seg = this.segmenter;
    if (!seg) return;
    // On a slow device the person outline is refreshed every other frame (the picture itself stays smooth).
    this.frameNo += 1;
    if (this.slow && this.frameNo % 2 === 1 && this.maskReady) return;
    const started = performance.now();
    const ts = Math.max(started, this.lastTimestamp + 1);
    this.lastTimestamp = ts;
    seg.segmentForVideo(this.video, ts, (result) => {
      const masks = result.confidenceMasks;
      const m = masks && masks.length > 0 ? masks[masks.length - 1] : undefined;
      if (!m) return;
      const values = m.getAsFloat32Array();
      if (this.mask.width !== m.width || this.mask.height !== m.height) {
        this.mask.width = m.width;
        this.mask.height = m.height;
      }
      const img = new ImageData(m.width, m.height);
      for (let i = 0; i < values.length; i++) img.data[i * 4 + 3] = maskAlpha(values[i]!);
      this.mask.getContext('2d')!.putImageData(img, 0, 0);
      this.maskReady = true;
    });
    this.slow = performance.now() - started > 24;
  }

  private draw(): void {
    const { videoWidth: w, videoHeight: h } = this.video;
    if (!w || !h) return;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    const filter = effectFilter(this.effect, performance.now());
    if (this.background.kind === 'none' || !this.segmenter) {
      this.ctx.filter = filter;
      this.ctx.drawImage(this.video, 0, 0, w, h);
      return;
    }
    try {
      this.segment();
    } catch {
      // A failed frame leaves the previous outline in place.
    }
    for (const c of [this.work, this.person]) {
      if (c.width !== w || c.height !== h) {
        c.width = w;
        c.height = h;
      }
    }
    const work = this.work.getContext('2d')!;
    // 1. Background: the blurred room, or the chosen picture filling the frame.
    work.filter = 'none';
    if (this.background.kind === 'blur') {
      const r = this.background.radius;
      work.filter = `blur(${r}px)`;
      // Drawn a little larger so the blur does not darken the edges.
      work.drawImage(this.video, -r * 2, -r * 2, w + r * 4, h + r * 4);
      work.filter = 'none';
    } else {
      const img = this.background.image;
      const scale = Math.max(w / img.width, h / img.height);
      work.drawImage(img, (w - img.width * scale) / 2, (h - img.height * scale) / 2, img.width * scale, img.height * scale);
    }
    // 2. The person, cut out with the soft mask.
    if (this.maskReady) {
      const p = this.person.getContext('2d')!;
      p.globalCompositeOperation = 'source-over';
      p.clearRect(0, 0, w, h);
      p.drawImage(this.video, 0, 0, w, h);
      p.globalCompositeOperation = 'destination-in';
      p.imageSmoothingEnabled = true;
      p.imageSmoothingQuality = 'high';
      p.drawImage(this.mask, 0, 0, w, h);
      p.globalCompositeOperation = 'source-over';
      work.drawImage(this.person, 0, 0);
    }
    // 3. Colour effect over the whole picture.
    this.ctx.filter = filter;
    this.ctx.drawImage(this.work, 0, 0);
  }

  /** Stops drawing and the canvas track; the camera track stays with its owner. */
  stop(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.output.stop();
    this.video.srcObject = null;
  }
}
